import {
  and,
  EntityName,
  eq,
  gt,
  lt,
  isNull,
  or,
  Property,
  ScopedProperties,
  FilterX,
} from '@debbie/common/dist/query/index.js'
import * as db from '@debbie/common/dist/query/db.js'
import { LanguageMap } from '@debbie/common/dist/lang/language-maps/index.js'
import {
  CaseVoucherPropertiesDisplay,
  CasePropertiesDisplay,
  ContactNameDisplay,
  ResponsibleDisplay,
  CaseEndReasonDisplay,
  CaseStatusDisplay,
  PausedUpdatesDisplay,
} from './Displays.js'
import { CaseStatus } from '@debbie/common/dist/cases/index.js'

const d = <D extends { [K in string]: React.ComponentType<{ value: any }> }>(
  displays: D,
): D => displays

export const displays = d({
  caseVoucherProperties: CaseVoucherPropertiesDisplay,
  caseProperties: CasePropertiesDisplay,
  contactName: ContactNameDisplay,
  responsible: ResponsibleDisplay,
  caseEndReason: CaseEndReasonDisplay,
  caseStatus: CaseStatusDisplay,
  updates: PausedUpdatesDisplay,
})

const v = <
  X extends {
    [Prop in Property<typeof db.schema>]?:
      | {
          label: (lang: LanguageMap) => string
          value: Prop
        }
      | {
          label: (lang: LanguageMap) => string
          customDisplay: keyof typeof displays
          values: Property<typeof db.schema>[]
          value: Prop
        }
  } & {
    [Prop in string]:
      | {
          label: (lang: LanguageMap) => string
          customDisplay: keyof typeof displays
          values: Property<typeof db.schema>[]
          scopedProperties?: ScopedProperties<typeof db.schema>
          value: Prop
        }
      // TODO : Cannot be used atm, since value should both match the key in views and the property that needs to be requested
      | {
          label: (lang: LanguageMap) => string
          value: Prop
        }
  },
>(
  x: X,
): X => x

export const views = v({
  'cases.id': {
    label: (lang) => lang.viewLabels['cases.id'],
    value: 'cases.id',
  },
  'cases.status': {
    label: (lang) => lang.viewLabels['cases.status'],
    customDisplay: 'caseStatus',
    values: ['cases.status', 'cases.endReasonId', 'cases.statusTime'],
    value: 'cases.status',
  },
  'creditors.id': {
    label: (lang) => lang.viewLabels['creditors.id'],
    value: 'creditors.id',
  },
  'creditors.name': {
    label: (lang) => lang.viewLabels['creditors.name'],
    value: 'creditors.name',
  },
  contactName: {
    label: (lang) => lang.viewLabels['contactName'],
    customDisplay: 'contactName',
    values: [],
    scopedProperties: {
      usersOnCustomers: {
        contactName: {
          fields: ['name'],
          markUnique: true,
          filters: eq('usersOnCustomers.contact', true),
        },
      },
    },
    value: 'contactName',
  },
  caseProperties: {
    label: (lang) => lang.viewLabels['caseProperties'],
    customDisplay: 'caseProperties',
    values: [
      'cases.caseGroupId',
      'propertyTypes.label',
      'propertyTypes.handle',
      'propertyTypes.scopeId',
    ],
    scopedProperties: {
      properties: {
        caseProperties: {
          fields: ['refId', 'type', 'data', 'handle'],
          requiredPath: ['cases'],
          filters: eq('properties.current', true),
        },
      },
    },
    value: 'caseProperties',
  },
  caseVoucherProperties: {
    label: (lang) => lang.viewLabels['caseVoucherProperties'],
    customDisplay: 'caseVoucherProperties',
    values: [
      'cases.caseGroupId',
      'propertyTypes.label',
      'propertyTypes.handle',
      'propertyTypes.scopeId',
    ],
    scopedProperties: {
      properties: {
        caseVoucherProperties: {
          fields: ['refId', 'type', 'data', 'handle'],
          requiredPath: ['caseVouchers'],
          filters: eq('properties.current', true),
        },
      },
    },
    value: 'caseVoucherProperties',
  },
  'billingModels.name': {
    label: (lang) => lang.viewLabels['billingModels.name'],
    value: 'billingModels.name',
  },
  'cases.createdAt': {
    label: (lang) => lang.viewLabels['cases.createdAt'],
    value: 'cases.createdAt',
  },
  'caseGroups.label': {
    label: (lang) => lang.viewLabels['caseGroups.label'],
    value: 'caseGroups.label',
  },
  'workflows.title': {
    label: (lang) => lang.viewLabels['workflows.title'],
    value: 'workflows.title',
  },
  'voucherTypes.label': {
    label: (lang) => lang.viewLabels['voucherTypes.label'],
    value: 'voucherTypes.label',
  },
  'caseVouchers.amount': {
    label: (lang) => lang.viewLabels['caseVouchers.amount'],
    value: 'caseVouchers.amount',
  },
  'cases.createdBy': {
    label: (lang) => lang.viewLabels['cases.createdBy'],
    value: 'cases.createdBy',
  },
  caseEndReason: {
    label: (lang) => lang.viewLabels['caseEndReason'],
    customDisplay: 'caseEndReason',
    values: ['cases.endReasonId', 'endReasons.label'],
    value: 'caseEndReason',
  },
  caseResponsible: {
    label: (lang) => lang.viewLabels['caseResponsible'],
    customDisplay: 'responsible',
    values: ['cases.responsibleReferenceId', 'cases.responsibleType'],
    value: 'caseResponsible',
  },
  caseUpdates: {
    label: (lang) => lang.viewLabels['caseUpdates'],
    customDisplay: 'updates',
    values: ['updates.updateTypeId'],
    scopedProperties: {
      updates: {
        pausedUpdates: {
          fields: ['updateTypeId'],
          filters: and(
            or(
              isNull('updates.continuedAt'),
              gt('updates.continuedAt', { fn: 'now' }),
            ),
            lt('updates.pausedAt', { fn: 'now' }),
          ),
        },
      },
    },
    value: 'caseUpdates',
  } as {
    label: (lang: LanguageMap) => string
    customDisplay: 'updates'
    values: 'updates.updateTypeId'[]
    scopedProperties: {
      updates: {
        pausedUpdates: {
          fields: 'updateTypeId'[]
          filters: FilterX<typeof db.schema>
        }
      }
    }
    value: string
  },
})

export const baseContext: {
  [K in EntityName]?: {
    availableViews: (keyof typeof views)[]
    itemId: Property<typeof db.schema>
    availableForQueue: (keyof typeof views)[]
    availableForOrdering: {
      key: keyof (typeof db.schema.entities)[K]['properties']
      label: (lang: LanguageMap) => string
    }[]
    preview: {
      header: keyof typeof views
      content: keyof typeof views
    }
  }
} = {
  cases: {
    availableViews: [
      'cases.id',
      'cases.status',
      'cases.createdAt',
      'cases.createdBy',
      'creditors.name',
      'billingModels.name',
      'caseGroups.label',
      'workflows.title',
      'caseVouchers.amount',
      'caseVoucherProperties',
      'caseProperties',
      'contactName',
      'caseResponsible',
      'caseEndReason',
      'caseUpdates',
    ],
    availableForOrdering: [
      { key: 'createdAt', label: (lang) => lang.viewLabels['cases.createdAt'] },
      { key: 'id', label: (lang) => lang.viewLabels['cases.id'] },
      { key: 'status', label: (lang) => lang.viewLabels['cases.status'] },
    ],
    availableForQueue: [
      'cases.id',
      'cases.status',
      'cases.createdAt',
      'cases.createdBy',
      'creditors.name',
      'billingModels.name',
      'caseGroups.label',
      'workflows.title',
      'caseVoucherProperties',
      'caseProperties',
      'contactName',
      'caseResponsible',
      'caseEndReason',
      'caseUpdates',
    ],
    itemId: 'cases.caseId',
    preview: {
      header: 'cases.id',
      content: 'contactName',
    },
  },
}
